import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, CalendarCheck, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, MORE_LINKS, SERVICES } from "../../data/site";

const MegaMenu = ({ onClose }) => (
  <div data-testid="services-mega-menu" className="absolute -left-6 top-full pt-4 w-[min(860px,92vw)]">
    <div className="glass rounded-3xl p-3 grid grid-cols-3 gap-1 rise">
      {SERVICES.map((s) => (
        <Link key={s.slug} to={`/services/${s.slug}`} onClick={onClose} data-testid={`mega-menu-link-${s.slug}`}
          className="group flex gap-3 rounded-2xl p-3.5 hover:bg-brand-50 transition-colors">
          <span className="h-10 w-10 rounded-xl bg-brand-100 text-brand-ink flex items-center justify-center shrink-0 group-hover:bg-brand-ink group-hover:text-white transition-colors">
            <s.icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="font-display text-[15px] font-medium text-navy leading-snug self-center">{s.title}</span>
        </Link>
      ))}
      <Link to="/simulateur" onClick={onClose} data-testid="mega-menu-simulator"
        className="flex items-center justify-between gap-2 rounded-2xl p-4 bg-navy text-white hover:bg-navy-700 transition-colors">
        <span className="font-display text-[15px]">Suis-je éligible ?</span><ArrowUpRight className="h-5 w-5" aria-hidden="true" />
      </Link>
    </div>
  </div>
);

const CtaButtons = ({ compact = false }) => (
  <>
    <Link to="/contact" data-testid={compact ? "mobile-quote-cta" : "nav-quote-cta"}
      className={`btn-outline whitespace-nowrap ${compact ? "flex-1 px-3 py-2.5 text-[15px]" : "px-5 py-2.5 text-[15px]"}`}>
      Demander un devis
    </Link>
    <Link to="/prendre-rdv" data-testid={compact ? "mobile-rdv-cta" : "nav-rdv-cta"}
      className={`btn-accent whitespace-nowrap ${compact ? "flex-1 px-3 py-2.5 text-[15px]" : "px-5 py-2.5 text-[15px]"}`}>
      <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Prendre RDV
    </Link>
  </>
);

const MobileMenu = ({ onClose }) => (
  <div id="mobile-menu" data-testid="mobile-menu" className="lg:hidden border-t border-brand/15 bg-white max-h-[75vh] overflow-y-auto">
    <nav className="container-x py-5 grid gap-6" aria-label="Menu mobile">
      <ul className="grid grid-cols-2 gap-2">
        {NAV_LINKS.map((l) => (
          <li key={l.to}><Link to={l.to} onClick={onClose} data-testid={`mobile-link-${l.to.slice(1)}`}
            className="block rounded-2xl bg-mist-50 border border-brand/15 px-4 py-3 font-display text-navy">{l.label}</Link></li>
        ))}
      </ul>
      <div>
        <p className="eyebrow mb-2">Services</p>
        <ul>{SERVICES.map((s) => (
          <li key={s.slug}><Link to={`/services/${s.slug}`} onClick={onClose} data-testid={`mobile-link-${s.slug}`}
            className="flex items-center gap-3 py-2.5 text-navy border-b border-slate-100"><s.icon className="h-5 w-5 text-brand-ink" aria-hidden="true" /> {s.title}</Link></li>
        ))}</ul>
      </div>
      <ul className="flex flex-wrap gap-2">
        {MORE_LINKS.map((l) => <li key={l.to}><Link to={l.to} onClick={onClose} data-testid={`mobile-link-${l.to.slice(1)}`} className="inline-block rounded-full border border-brand/25 px-4 py-2 text-sm text-navy">{l.label}</Link></li>)}
      </ul>
    </nav>
  </div>
);

export const Header = () => {
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => { setMega(false); setMobile(false); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (setMega(false), setMobile(false));
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setMega(false);
    const onScroll = () => setScrolled(window.scrollY > 8);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); window.removeEventListener("scroll", onScroll); };
  }, []);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-full font-display text-[15px] whitespace-nowrap transition-colors ${isActive ? "text-brand-ink bg-brand-50" : "text-navy hover:text-brand-ink"}`;

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled || mobile ? "bg-white/75 backdrop-blur-xl shadow-[0_1px_0_rgba(95,134,179,0.18)]" : "bg-white/40 backdrop-blur-md"}`}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 bg-navy text-white px-4 py-2 rounded-lg">Aller au contenu</a>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8 flex items-center justify-between gap-4 h-[72px]" ref={ref}>
        <Logo />
        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Navigation principale">
          {NAV_LINKS.slice(0, 2).map((l) => <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.to.slice(1)}`}>{l.label}</NavLink>)}
          <div className="relative" onMouseEnter={() => setMega(true)} onMouseLeave={() => setMega(false)}>
            <button type="button" data-testid="nav-services-toggle" aria-expanded={mega} aria-controls="services-menu"
              onClick={(e) => setMega((v) => (e.detail === 0 ? !v : true))}
              className="px-3 py-2 rounded-full font-display text-[15px] text-navy hover:text-brand-ink inline-flex items-center gap-1 whitespace-nowrap">
              Services <ChevronDown className={`h-4 w-4 transition-transform ${mega ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {mega && <div id="services-menu"><MegaMenu onClose={() => setMega(false)} /></div>}
          </div>
          {NAV_LINKS.slice(2).map((l) => <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-link-${l.to.slice(1)}`}>{l.label}</NavLink>)}
        </nav>
        <div className="hidden md:flex items-center gap-2"><CtaButtons /></div>
        <button type="button" className="lg:hidden inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white/80 px-4 py-2 font-display text-navy"
          aria-expanded={mobile} aria-controls="mobile-menu" data-testid="mobile-menu-toggle" onClick={() => setMobile((v) => !v)}>
          {mobile ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />} Menu
        </button>
      </div>
      <div className="md:hidden container-x pb-3 flex gap-2"><CtaButtons compact /></div>
      {mobile && <MobileMenu onClose={() => setMobile(false)} />}
    </header>
  );
};
